<template>
	<div class="checkout">
		<Banner :brand="brand" :margin-bottom="false" />

		<div
			v-if="processingPaypal"
			class="checkout__processing"
			role="alert"
			aria-busy="true"
		>
			<BSpinner />
			<h2>Processing your PayPal order</h2>
		</div>

		<template v-if="pageReady">
			<BContainer>
				<VSWell
					v-if="errors.length > 0"
					variant="error"
					class="checkout__error"
				>
					<span v-html="errors[0]"></span>
				</VSWell>

				<CheckoutDesktop
					v-if="isDesktop"
					:countdown-duration="countdown"
					:carousel-items="carousel.items"
					:carousel-description="carousel.description"
					:review-items="reviews"
				/>
			</BContainer>

			<CheckoutMobile
				v-if="isMobile"
				:countdown-duration="countdown"
				:carousel-items="carousel.items"
				:carousel-description="carousel.description"
				:review-items="reviews"
			/>
		</template>

		<Loading v-else />
	</div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { VSWell } from '@component/UI';
import Banner from '@component/user-backend/Checkout/V4/banners/Banner.vue';
import Loading from '@component/user-backend/Checkout/V4/Loading.vue';
import CheckoutDesktop from '@component/user-backend/Checkout/V4/CheckoutDesktop.vue';
import CheckoutMobile from '@component/user-backend/Checkout/V4/CheckoutMobile.vue';
import api from '@js/api';

export default {
	components: {
		Banner,
		Loading,
		CheckoutDesktop,
		CheckoutMobile,
		VSWell,
	},

	props: {
		brand: {
			type: String,
			default: 'sculptnation',
		},
		countdown: {
			type: Number,
			default: 0,
		},
		carousel: {
			type: Object,
			default: () => ({ items: [], description: '' }),
		},
		reviews: {
			type: Array,
			default: () => [],
		},
		bumpers: {
			type: Array,
			default: () => [],
		},
		params: {
			type: Object,
			default: () => ({}),
		},
		paymentProcessors: {
			type: Array,
			default: () => [],
		},
		isEcommerce: {
			type: Boolean,
			default: false,
		},
	},

	computed: {
		...mapState('checkout', [
			'cart',
			'billingAddress',
			'shippingAddress',
			'processingPaypal',
			'errors',
			'shippingDifferentFromBilling',
		]),
		...mapGetters('checkout', ['pageReady', 'isDigitalProduct']),
		...mapGetters('responsive', ['isMobile', 'isDesktop']),
	},

	watch: {
		billingAddress() {
			this.refreshTaxAndShipping();
		},
		shippingAddress() {
			this.refreshTaxAndShipping();
		},
		shippingDifferentFromBilling() {
			this.refreshTaxAndShipping();
		},
		isDigitalProduct() {
			if (this.isDigitalProduct) {
				return;
			}
			this.refreshTaxAndShipping();
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},
	},

	async beforeMount() {
		this.setBrand(this.brand);
		this.setBumpers(this.bumpers);
		this.setIsEcommerce(this.isEcommerce);
		this.setPaymentProcessors(this.paymentProcessors);

		if (this.$route) {
			if (this.$route.name === 'discount-checkout') {
				const cart = (
					await api.getCartByDiscount(this.$route.params.discount)
				).data;

				this.setCart(cart);
				// enable ContactInformation to prompt guest to log in
				this.setIsEcommerce(true);
			} else {
				this.setParams(this.$route.params);
				this.setQuery(this.$route.query);
			}
		} else {
			this.setParams(this.params);
		}

		this.initialize();
	},

	mounted() {
		const query = window.matchMedia('(max-width: 992px)');

		const updateViewportSize = () => {
			this.setViewportSize(query.matches ? 'mobile' : 'desktop');
		};

		updateViewportSize();

		query.addEventListener('change', updateViewportSize);
	},

	methods: {
		...mapMutations('checkout', [
			'setCart',
			'setQuery',
			'setParams',
			'setBumpers',
			'setPaymentProcessors',
			'setBrand',
			'setIsEcommerce',
		]),
		...mapMutations('responsive', ['setViewportSize']),
		...mapActions('checkout', ['initialize', 'refreshTaxAndShipping']),
	},
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700&display=swap');

.checkout {
	--black: #000;
	--white: #fff;

	--gray-100: #f8f9fa;
	--gray-200: #e9ecef;
	--gray-300: #dee2e6;
	--gray-400: #ced4da;
	--gray-500: #adb5bd;
	--gray-600: #6c757d;
	--gray-700: #495057;
	--gray-800: #343a40;
	--gray-900: #212529;

	--blue-100: #e7f1ff;
	--blue-200: #9ec5fe;
	--blue-300: #6ea8fe;
	--blue-400: #3d8bfd;
	--blue-500: #0d6efd;
	--blue-600: #0d6efd;
	--blue-700: #084298;
	--blue-800: #052c65;
	--blue-900: #031633;

	--green-100: #d1e7dd;
	--green-200: #a3cfbb;
	--green-300: #75b798;
	--green-400: #479f76;
	--green-500: #198754;
	--green-600: #146c43;
	--green-700: #0f5132;
	--green-800: #0a3622;
	--green-900: #051b11;

	--red-100: #f8d7da;
	--red-200: #f1aeb5;
	--red-300: #ea868f;
	--red-400: #e35d6a;
	--red-500: #dc3545;
	--red-600: #b02a37;
	--red-700: #842029;
	--red-800: #58151c;
	--red-900: #2c0b0e;

	--shadow-xs: 0px 1px 2px -1px rgba(0, 0, 0, 0.1),
		0px 1px 3px 0px rgba(0, 0, 0, 0.1);
	--shadow-sm: 0px 1px 2px -1px rgba(0, 0, 0, 0.1),
		0px 1px 3px 0px rgba(0, 0, 0, 0.1);
	--shadow-base: 0px 2px 4px -2px rgba(0, 0, 0, 0.1),
		0px 4px 6px -1px rgba(0, 0, 0, 0.1);
	--shadow-md: 0px 4px 6px -4px rgba(0, 0, 0, 0.1),
		0px 10px 15px -3px rgba(0, 0, 0, 0.1);
	--shadow-lg: 0px 8px 10px -6px rgba(0, 0, 0, 0.1),
		0px 20px 25px -5px rgba(0, 0, 0, 0.1);
	--shadow-xl: 0px 25px 50px -12px rgba(0, 0, 0, 0.25);

	--size-xs: 4px;
	--size-sm: 8px;
	--size-base: 14px;
	--size-md: 16px;
	--size-lg: 24px;
	--size-xl: 32px;
	--size-xxl: 48px;

	--custom-control-height: 50px;

	font-family: var(--font-family-sans-serif);
	margin-bottom: var(--size-xxl);
	color: var(--gray-800);

	&__processing {
		position: fixed;
		background: rgba(255, 255, 255, 0.5);
		backdrop-filter: blur(var(--size-sm));
		inset: 0;
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: column;
		gap: var(--size-lg);
		z-index: 101; // this needs to be a higher index than the paypal button which is set to 100

		> h2 {
			font-size: var(--size-lg);

			@media screen and (max-width: 992px) {
				font-size: var(--size-md);
			}
		}
	}

	@keyframes shimmer {
		0% {
			transform: translateX(-100%);
		}
		100% {
			transform: translateX(100%);
		}
	}

	&__error {
		margin: var(--size-md) 0;
	}
}
</style>
